import { useState, useMemo } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { useListCustomersQuery } from '@/store/customersApi';
import { useListSprawyQuery } from '@/store/sprawyApi';
import {
  AssignmentSection,
  AssignmentRow,
  AssignmentField,
} from './DocumentUpload.styled';

export interface UploadAssignment {
  customerId?: string;
  sprawaId?: string;
}

interface FileWithName {
  file: File;
  documentName: string;
}

interface DocumentUploadModalProps {
  files: File[];
  show: boolean;
  onConfirm: (files: FileWithName[], assignment: UploadAssignment) => void;
  onCancel: () => void;
}

interface SelectOption {
  value: string;
  label: string;
}

function removeExtension(fileName: string): string {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot > 0 ? fileName.substring(0, lastDot) : fileName;
}

function getDefaultName(file: File): string {
  const relativePath = file.webkitRelativePath;
  if (relativePath) {
    return removeExtension(relativePath);
  }
  return removeExtension(file.name);
}

function ModalContent({
  files,
  onConfirm,
  onCancel,
}: Omit<DocumentUploadModalProps, 'show'>) {
  const { t } = useTranslation();
  const [names, setNames] = useState<string[]>(() =>
    files.map((f) => getDefaultName(f)),
  );
  const [autoMatch, setAutoMatch] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState<SelectOption | null>(null);
  const [selectedSprawa, setSelectedSprawa] = useState<SelectOption | null>(null);
  const [customerSearch, setCustomerSearch] = useState('');

  const { data: customersData } = useListCustomersQuery({
    page: 1,
    pageSize: 50,
    search: customerSearch || undefined,
  });

  const { data: sprawyData } = useListSprawyQuery({
    page: 1,
    pageSize: 50,
    customerId: selectedCustomer?.value,
  });

  const customerOptions: SelectOption[] = useMemo(
    () =>
      customersData?.items.map((c) => ({
        value: c.id,
        label: c.name || `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim(),
      })) ?? [],
    [customersData],
  );

  const sprawaOptions: SelectOption[] = useMemo(
    () =>
      sprawyData?.items.map((s) => ({
        value: s.id,
        label: [s.signature, s.court].filter(Boolean).join(' — '),
      })) ?? [],
    [sprawyData],
  );

  const updateName = (index: number, name: string) => {
    setNames((prev) => prev.map((n, i) => (i === index ? name : n)));
  };

  const handleAutoMatchChange = (checked: boolean) => {
    setAutoMatch(checked);
    if (checked) {
      setSelectedCustomer(null);
      setSelectedSprawa(null);
    }
  };

  const handleCustomerChange = (option: SelectOption | null) => {
    setSelectedCustomer(option);
    setSelectedSprawa(null);
    if (option) setAutoMatch(false);
  };

  const handleSprawaChange = (option: SelectOption | null) => {
    setSelectedSprawa(option);
    if (option) setAutoMatch(false);
  };

  const handleConfirm = () => {
    const assignment: UploadAssignment = {};
    if (!autoMatch) {
      if (selectedCustomer) assignment.customerId = selectedCustomer.value;
      if (selectedSprawa) assignment.sprawaId = selectedSprawa.value;
    }
    onConfirm(
      files.map((file, i) => ({ file, documentName: names[i] })),
      assignment,
    );
  };

  return (
    <>
      <Modal.Header closeButton>
        <Modal.Title>{t('upload.modal.title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <AssignmentSection>
          <Form.Check
            type="checkbox"
            id="auto-match-checkbox"
            label={t('upload.modal.autoMatch')}
            checked={autoMatch}
            onChange={(e) => handleAutoMatchChange(e.target.checked)}
            className="mb-3"
          />
          <AssignmentRow>
            <AssignmentField>
              <label>{t('upload.modal.assignCustomer')}</label>
              <Select<SelectOption>
                isClearable
                isDisabled={autoMatch}
                value={selectedCustomer}
                onChange={handleCustomerChange}
                options={customerOptions}
                onInputChange={setCustomerSearch}
                placeholder={t('upload.modal.searchCustomer')}
                noOptionsMessage={() => t('upload.modal.noOptions')}
              />
            </AssignmentField>
            <AssignmentField>
              <label>{t('upload.modal.assignSprawa')}</label>
              <Select<SelectOption>
                isClearable
                isDisabled={autoMatch}
                value={selectedSprawa}
                onChange={handleSprawaChange}
                options={sprawaOptions}
                placeholder={t('upload.modal.searchSprawa')}
                noOptionsMessage={() => t('upload.modal.noOptions')}
              />
            </AssignmentField>
          </AssignmentRow>
        </AssignmentSection>

        <p className="text-muted mb-3">{t('upload.modal.description')}</p>
        {files.map((file, idx) => (
          <div key={`${file.name}-${idx}`} className={idx > 0 ? 'mt-3' : ''}>
            <div className="d-flex align-items-center gap-2 mb-1">
              <span className="text-muted small fw-semibold text-nowrap">{t('upload.modal.fileName')}:</span>
              <span className="text-muted small">{file.name}</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small fw-semibold text-nowrap">{t('upload.modal.yourName')}:</span>
              <Form.Control
                size="sm"
                type="text"
                value={names[idx]}
                onChange={(e) => updateName(idx, e.target.value)}
                placeholder={t('upload.modal.namePlaceholder')}
              />
            </div>
          </div>
        ))}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button variant="primary" onClick={handleConfirm}>
          {t('upload.modal.upload')}
        </Button>
      </Modal.Footer>
    </>
  );
}

export default function DocumentUploadModal({
  files,
  show,
  onConfirm,
  onCancel,
}: DocumentUploadModalProps) {
  const key = files.map((f) => f.name).join('|');

  return (
    <Modal show={show} onHide={onCancel} size="xl" centered dialogClassName="modal-fit-content">
      {show && (
        <ModalContent
          key={key}
          files={files}
          onConfirm={onConfirm}
          onCancel={onCancel}
        />
      )}
    </Modal>
  );
}

export type { FileWithName };
